import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useCart } from '../context/CartContext';

/**
 * FlyingProductAnimation Component
 * Renders the flying product image animation
 */
const FlyingProductAnimation = ({ imageUrl, startPosition, onComplete }) => {
  const flyingRef = useRef(null);
  
  useEffect(() => {
    if (!startPosition || !flyingRef.current) {
      onComplete?.();
      return;
    }
    
    // Find the cart icon position
    const cartIcon = document.querySelector('.cart-icon-btn');
    if (!cartIcon) {
      onComplete?.();
      return;
    }
    
    const cartRect = cartIcon.getBoundingClientRect();
    const startX = startPosition.x;
    const startY = startPosition.y;
    const endX = cartRect.left + cartRect.width / 2;
    const endY = cartRect.top + cartRect.height / 2;
    
    // Calculate curved path control point (arc above)
    const controlX = (startX + endX) / 2;
    const controlY = Math.min(startY, endY) - 80;
    
    // Animate using requestAnimationFrame for smooth 60fps
    const duration = 550; // ms - keep under 600ms
    const startTime = performance.now();
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (ease-out cubic with slight overshoot)
      const easeProgress = progress < 1 
        ? 1 - Math.pow(1 - progress, 3) 
        : 1;
      
      // Quadratic Bezier curve for smooth curved path
      const t = easeProgress;
      const x = Math.pow(1 - t, 2) * startX + 2 * (1 - t) * t * controlX + Math.pow(t, 2) * endX;
      const y = Math.pow(1 - t, 2) * startY + 2 * (1 - t) * t * controlY + Math.pow(t, 2) * endY;
      
      // Scale down as it approaches cart (1 -> 0.3)
      const scale = 1 - (progress * 0.7);
      
      // Slight fade as it approaches (1 -> 0.8)
      const opacity = 1 - (progress * 0.2);
      
      // Rotation for dynamic feel
      const rotation = progress * 180;
      
      if (flyingRef.current) {
        flyingRef.current.style.left = `${x}px`;
        flyingRef.current.style.top = `${y}px`;
        flyingRef.current.style.transform = `translate(-50%, -50%) scale(${scale}) rotate(${rotation}deg)`;
        flyingRef.current.style.opacity = `${opacity}`;
      }
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        onComplete?.();
      }
    };
    
    // Start animation
    requestAnimationFrame(animate);
    
  }, [startPosition, onComplete]);
  
  if (!startPosition) return null;
  
  return createPortal(
    <div 
      ref={flyingRef}
      className="flying-product-animation"
      style={{
        position: 'fixed',
        left: startPosition.x,
        top: startPosition.y,
        width: '70px',
        height: '70px',
        zIndex: 10000,
        pointerEvents: 'none',
        willChange: 'transform, left, top, opacity',
        transform: 'translate(-50%, -50%) scale(1)',
      }}
    >
      <img 
        src={imageUrl} 
        alt=""
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: '10px',
          boxShadow: '0 8px 24px rgba(0, 69, 52, 0.35), 0 0 0 3px rgba(0, 128, 96, 0.3)',
        }}
      />
    </div>,
    document.body
  );
};

/**
 * MiniCartNotification Component
 * Shows a slide-in notification after adding to cart
 */
const MiniCartNotification = ({ isVisible, onClose, productName, productPrice, productImage }) => {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        onClose?.();
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);
  
  if (!productName) return null;
  
  return createPortal(
    <div className={`mini-cart-notification ${isVisible ? 'visible' : ''}`}>
      <div className="mini-cart-content">
        <div className="mini-cart-check">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <circle cx="9" cy="9" r="9" fill="#22c55e"/>
            <path d="M5 9l3 3 5-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        {productImage && (
          <div className="mini-cart-image">
            <img src={productImage} alt={productName} />
          </div>
        )}
        <div className="mini-cart-details">
          <span className="mini-cart-title">Added to Cart!</span>
          <span className="mini-cart-product">{productName}</span>
          {productPrice && <span className="mini-cart-price">{productPrice}</span>}
        </div>
        <button className="mini-cart-close" onClick={onClose} aria-label="Close notification">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M11 3L3 11M3 3l8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>
      <div className="mini-cart-progress" />
    </div>,
    document.body
  );
};

/**
 * CartAnimationProvider Component
 * Renders all cart-related animations globally
 */
const CartAnimationProvider = () => {
  const { 
    flyingAnimation, 
    clearFlyingAnimation,
    miniCartData,
    showMiniCart,
    closeMiniCart
  } = useCart();
  
  return (
    <>
      {flyingAnimation && (
        <FlyingProductAnimation
          imageUrl={flyingAnimation.imageUrl}
          startPosition={flyingAnimation.startPosition}
          onComplete={clearFlyingAnimation}
        />
      )}
      <MiniCartNotification
        isVisible={showMiniCart}
        onClose={closeMiniCart}
        productName={miniCartData?.name}
        productPrice={miniCartData?.price}
        productImage={miniCartData?.image}
      />
    </>
  );
};

export default CartAnimationProvider;
